import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { BookOpenIcon, GraduationCapIcon, ClockIcon, TestTubeIcon } from 'lucide-react';
import { PaymentModal } from './PaymentModal';
export function Courses() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const [selectedCourse, setSelectedCourse] = useState<string | null>(null);
  const [selectedPrice, setSelectedPrice] = useState<number | undefined>(undefined);
  const [isSpecialized, setIsSpecialized] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const handleEnrollClick = (courseTitle: string, price?: number, specialized: boolean = false) => {
    setSelectedCourse(courseTitle);
    setSelectedPrice(price);
    setIsSpecialized(specialized);
    setIsPaymentModalOpen(true);
  };
  const courses = [{
    title: 'A1.1 - Beginner',
    level: 'Beginner',
    duration: '4 weeks',
    description: 'Start your language journey with essential vocabulary and basic grammar structures.',
    outcomes: ['Basic greetings and introductions', 'Simple conversations', 'Foundation building', 'DELF A1 preparation']
  }, {
    title: 'A1.2 - Beginner',
    level: 'Beginner',
    duration: '4 weeks',
    description: 'Complete your beginner level with expanded vocabulary and communication skills.',
    outcomes: ['Daily life conversations', 'Basic reading comprehension', "DELF A1 (Diplôme d'étude en langue française)"]
  }, {
    title: 'A2.1 - Elementary',
    level: 'Elementary',
    duration: '4 weeks',
    description: 'Build on your foundation with more complex sentence structures and vocabulary.',
    outcomes: ['Describe experiences', 'Express opinions', 'Intermediate grammar']
  }, {
    title: 'A2.2 - Elementary',
    level: 'Elementary',
    duration: '4 weeks',
    description: 'Complete elementary level with confidence in everyday communication.',
    outcomes: ['Routine conversations', 'Reading simple texts', "DELF A2 (Diplôme d'étude en langue française)"]
  }, {
    title: 'B1.1 - Lower Intermediate',
    level: 'Intermediate',
    duration: '4 weeks',
    description: 'Develop fluency with complex topics and professional communication.',
    outcomes: ['Work-related discussions', 'Express ideas clearly', 'Advanced grammar']
  }, {
    title: 'B1.2 - Lower Intermediate',
    level: 'Intermediate',
    duration: '4 weeks',
    description: 'Master lower intermediate level with comprehensive language skills.',
    outcomes: ['Professional communication', 'Complex texts', "DELF B1 (Diplôme d'étude en langue française)"]
  }, {
    title: 'B2.1 - Higher Intermediate',
    level: 'Intermediate',
    duration: '4 weeks',
    description: 'Achieve higher intermediate proficiency for academic and professional contexts.',
    outcomes: ['Detailed discussions', 'Academic writing', 'Nuanced expression']
  }, {
    title: 'B2.2 - Higher Intermediate',
    level: 'Intermediate',
    duration: '4 weeks',
    description: 'Complete higher intermediate level with near-fluent communication abilities.',
    outcomes: ['Professional fluency', 'Complex arguments', "DELF B2 (Diplôme d'étude en langue française)"]
  }, {
    title: 'C1.1 - Advanced',
    level: 'Advanced',
    duration: '4 weeks',
    description: 'Master advanced language skills for professional and academic excellence.',
    outcomes: ['Native-like fluency', 'Professional presentations', 'Advanced comprehension']
  }, {
    title: 'C1.2 - Advanced',
    level: 'Advanced',
    duration: '4 weeks',
    description: 'Complete advanced level with mastery in all aspects of the language.',
    outcomes: ['Expert communication', 'Academic excellence', 'DALF C1 (Diplôme approfondi de langue française)']
  }, {
    title: 'C2.1 - Proficiency',
    level: 'Advanced',
    duration: '4 weeks',
    description: 'Achieve near-native proficiency with sophisticated language use.',
    outcomes: ['Mastery of complex texts', 'Subtle nuances', 'Professional excellence']
  }, {
    title: 'C2.2 - Proficiency',
    level: 'Advanced',
    duration: '4 weeks',
    description: 'Complete mastery of the language at the highest level.',
    outcomes: ['Native-level fluency', 'Cultural mastery', 'DALF C2 (Diplôme approfondi de langue française)']
  }];
  const specialCourses = [{
    title: 'TEF Preparation',
    level: 'Specialized',
    duration: '24hrs a Month',
    description: 'Comprehensive TEF exam preparation with conversation and grammar classes.',
    outcomes: ['TEF exam strategies', 'Conversation practice', 'Grammar mastery', 'Mock exams'],
    price: 170000,
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/5zGNzTgwFDschqMGVffWwR/3.png'
  }, {
    title: 'French for Kids (FFK)',
    level: 'Kids',
    duration: '1 month',
    description: 'Fun and interactive French lessons designed specifically for children aged 5 and above.',
    outcomes: ['Course materials included', 'Course books included', 'Interactive sessions', 'Dedicated trained tutor', 'Active learning approach'],
    price: 110000,
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/6oPxkYeFELBQSp3GsF4gCE/4.png'
  }, {
    title: 'Corporate Bilingual Training',
    level: 'Corporate',
    duration: 'Flexible',
    description: 'Professional language training for businesses and organizations looking to upskill their teams.',
    outcomes: ['Customized corporate curriculum', 'Flexible scheduling for business needs', 'Progress tracking and reporting', 'Professional certification preparation', 'Priority corporate support', 'On-site or virtual options'],
    price: 400000,
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/ewujXjSzyA8FdMGL98WnxN/5.png'
  }, {
    title: 'Business Elite Group Course',
    level: 'Corporate',
    duration: 'Flexible',
    description: 'Exclusive corporate training package for companies looking to upskill their teams. Perfect for businesses investing in employee language development.',
    outcomes: ['Dedicated group training for 6 employees', 'Customized corporate curriculum', 'Flexible scheduling for business needs', 'Progress tracking and reporting', 'Professional certification preparation', 'Priority corporate support'],
    price: 400000,
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/rbEHJYSVBLfMGtd3xv4i48/6.png'
  }];
  return <>
      <section id="courses" className="py-24 bg-gradient-to-br from-green-50 to-orange-50" data-id="element-107">
        <div className="max-w-7xl mx-auto px-6" data-id="element-108">
          <motion.div ref={ref} initial={{
          opacity: 0,
          y: 50
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.8
        }} className="text-center mb-16" data-id="element-109">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6" data-id="element-110">
              Our <span className="text-green-600" data-id="element-111">Courses</span>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8" data-id="element-112">
              Comprehensive 4-week language programs for French, English,
              German, and Spanish. Each full level takes 2 months to complete.
            </p>
            <a href="https://apprendre.tv5monde.com/fr/exercices/a1-debutant" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-orange-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-orange-700 transition-colors duration-200 shadow-lg hover:shadow-xl" data-id="element-113">
              <TestTubeIcon size={24} data-id="element-114" />
              Take a Test to Know Your Level
            </a>
          </motion.div>
          {/* Specialized Programs */}
          <div className="mb-16" data-id="element-115">
            <h3 className="text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-116">
              Specialized <span className="text-orange-600" data-id="element-117">Programs</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-118">
              {specialCourses.map((course, index) => <motion.div key={course.title} initial={{
              opacity: 0,
              y: 30
            }} animate={inView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.6,
              delay: index * 0.1
            }} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 group cursor-pointer" data-id="element-119">
                  {course.image && <img src={course.image} alt={course.title} className="w-full h-48 object-cover rounded-xl mb-6" data-id="element-120" />}
                  <div className="flex items-center justify-between mb-4" data-id="element-121">
                    <span className={`${course.level === 'Corporate' ? 'bg-slate-900 text-white' : 'bg-orange-100 text-orange-700'} px-4 py-1 rounded-full text-sm font-semibold`} data-id="element-122">
                      {course.level}
                    </span>
                    <BookOpenIcon className={`${course.level === 'Corporate' ? 'text-slate-900' : 'text-orange-600'} group-hover:scale-110 transition-transform duration-300`} size={24} data-id="element-123" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-124">
                    {course.title}
                  </h3>
                  <div className="flex items-center gap-2 text-slate-600 mb-4" data-id="element-125">
                    <ClockIcon size={16} data-id="element-126" />
                    <span className="text-sm" data-id="element-127">{course.duration}</span>
                  </div>
                  <p className="text-slate-600 mb-6 text-sm" data-id="element-128">
                    {course.description}
                  </p>
                  <div className="border-t border-slate-200 pt-4" data-id="element-129">
                    <div className="flex items-center gap-2 mb-3" data-id="element-130">
                      <GraduationCapIcon size={18} className={course.level === 'Corporate' ? 'text-slate-900' : 'text-orange-600'} data-id="element-131" />
                      <span className="text-sm font-semibold text-slate-700" data-id="element-132">
                        What's Included:
                      </span>
                    </div>
                    <ul className="space-y-2 mb-6" data-id="element-133">
                      {course.outcomes.slice(0, 3).map((outcome, i) => <li key={i} className="text-sm text-slate-600 flex items-center gap-2" data-id="element-134">
                          <span className={`w-1.5 h-1.5 ${course.level === 'Corporate' ? 'bg-slate-900' : 'bg-orange-600'} rounded-full`} data-id="element-135" />
                          {outcome}
                        </li>)}
                    </ul>
                    {course.title === 'French for Kids (FFK)' && <p className="text-xs text-slate-600 mb-4 italic" data-id="element-136">
                        * Child must be 5 years old and above to register
                      </p>}
                    {course.title === 'Business Elite Group Course' && <p className="text-xs text-slate-600 mb-4 italic" data-id="element-137">
                        * For companies registering exactly 6 employees
                      </p>}
                    <div className="text-2xl font-bold text-orange-600 mb-4" data-id="element-138">
                      ₦{course.price.toLocaleString()}
                    </div>
                  </div>
                  <button onClick={() => handleEnrollClick(course.title, course.price, true)} className={`w-full mt-6 ${course.level === 'Corporate' ? 'bg-slate-900 hover:bg-slate-800' : 'bg-orange-600 hover:bg-orange-700'} text-white py-3 rounded-full font-semibold transition-colors duration-200`} data-id="element-139">
                    {course.level === 'Corporate' ? 'Register Your Team' : 'Enroll Now'}
                  </button>
                </motion.div>)}
            </div>
          </div>
          {/* Language Levels - 2 per row */}
          <h3 className="text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-140">
            Language <span className="text-green-600" data-id="element-141">Levels</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-142">
            {courses.map((course, index) => <motion.div key={course.title} initial={{
            opacity: 0,
            y: 30
          }} animate={inView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6,
            delay: index * 0.05
          }} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 group cursor-pointer" data-id="element-143">
                <div className="flex items-center justify-between mb-4" data-id="element-144">
                  <span className="bg-green-100 text-green-700 px-4 py-1 rounded-full text-sm font-semibold" data-id="element-145">
                    {course.level}
                  </span>
                  <BookOpenIcon className="text-orange-600 group-hover:scale-110 transition-transform duration-300" size={24} data-id="element-146" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-147">
                  {course.title}
                </h3>
                <div className="flex items-center gap-2 text-slate-600 mb-4" data-id="element-148">
                  <ClockIcon size={16} data-id="element-149" />
                  <span className="text-sm" data-id="element-150">{course.duration}</span>
                </div>
                <p className="text-slate-600 mb-6" data-id="element-151">{course.description}</p>
                <div className="border-t border-slate-200 pt-4" data-id="element-152">
                  <div className="flex items-center gap-2 mb-3" data-id="element-153">
                    <GraduationCapIcon size={18} className="text-green-600" data-id="element-154" />
                    <span className="text-sm font-semibold text-slate-700" data-id="element-155">
                      What You Will Achieve:
                    </span>
                  </div>
                  <ul className="space-y-2" data-id="element-156">
                    {course.outcomes.map((outcome, i) => <li key={i} className="text-sm text-slate-600 flex items-center gap-2" data-id="element-157">
                        <span className="w-1.5 h-1.5 bg-green-600 rounded-full" data-id="element-158" />
                        {outcome}
                      </li>)}
                  </ul>
                </div>
                <button onClick={() => handleEnrollClick(course.title, undefined, false)} className="w-full mt-6 bg-green-600 text-white py-3 rounded-full font-semibold hover:bg-green-700 transition-colors duration-200" data-id="element-159">
                  Enroll Now
                </button>
              </motion.div>)}
          </div>
        </div>
      </section>
      <PaymentModal isOpen={isPaymentModalOpen} onClose={() => setIsPaymentModalOpen(false)} courseTitle={selectedCourse || ''} price={selectedPrice} isSpecialized={isSpecialized} data-id="element-160" />
    </>;
}